import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OrgOnboardingModal from '../onboarding/OrgOnboardingModal';
import '../../assets/styles/modern-sidebar.css';

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start closed on mobile
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organisation, setOrganisation, logout } = useAuth();

  // A brand new org (onboardingCompletedAt still null) gets prompted once to
  // pick what it wants to track, so the dashboard/sidebar can be tailored
  // instead of showing every category by default. Only an admin can set
  // this - other roles just see the default (unfiltered) view until then.
  // super_admin is excluded - its "organisation" is just a platform-internal
  // placeholder, not a real tenant with categories to track.
  const needsOnboarding = !!organisation && !organisation.onboardingCompletedAt
    && user?.accessLevel === 'admin' && user?.role !== 'super_admin';
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    const sidebar = document.querySelector('.modern-sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    const main = document.querySelector('.app-main');
    const footer = document.querySelector('.app-footer');
    const header = document.querySelector('.app-header');
    
    if (sidebar) {
      // Check if we're on mobile
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // Mobile behavior: show/hide sidebar with overlay
        sidebar.classList.toggle('open');
        if (overlay) {
          overlay.classList.toggle('active');
        }
      } else {
        // Desktop behavior: collapse/expand sidebar
        sidebar.classList.toggle('collapsed');
        
        // Update main content margin
        if (main) {
          main.style.marginLeft = sidebar.classList.contains('collapsed') ? '70px' : '240px';
        }
        
        // Update footer position
        if (footer) {
          footer.style.left = sidebar.classList.contains('collapsed') ? '70px' : '240px';
        }
        
        // Update header position
        if (header) {
          header.style.left = sidebar.classList.contains('collapsed') ? '70px' : '240px';
        }
      }
    }
  };
  
  // Close sidebar when clicking overlay
  const closeSidebar = () => {
    setSidebarOpen(false);
    const sidebar = document.querySelector('.modern-sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    
    if (sidebar) {
      sidebar.classList.remove('open');
    }
    if (overlay) {
      overlay.classList.remove('active');
    }
  };
  
  // Use actual user data from auth context with fallback
  const userData = user || {
    firstName: 'User',
    lastName: '',
    email: '',
    role: 'viewer',
    organisation: { name: 'Organisation' }
  };

  // Roles that can see "My Tasks"
  const canViewMyTasks = ['field_officer', 'hse_manager', 'safety_officer', 'environmental_officer', 'field_inspector'];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-wrapper">
      {/* Sidebar Overlay for Mobile */}
      <div className="sidebar-overlay" onClick={closeSidebar}></div>
      
      {/* Navbar */}
      <nav className="app-header">
        {/* Left navbar links */}
        <div className="header-left">
          <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
            <i className="fas fa-bars"></i>
          </button>
          <span className="header-title">Dashboard</span>
        </div>

        {/* Right navbar links */}
        <div className="header-right">
          {/* Notifications */}
          <button className="header-icon-btn">
            <i className="fas fa-bell"></i>
            <span className="notification-badge">3</span>
          </button>

          {/* User Menu */}
          <div className="header-user-menu" style={{ position: 'relative' }}>
            <button 
              className="header-user-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar-small">
                <i className="fas fa-user-circle"></i>
              </div>
              <span className="user-name-small">{userData.firstName} {userData.lastName}</span>
              <i className="fas fa-chevron-down"></i>
            </button>
            {showUserMenu && (
              <div className="user-dropdown" style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '200px',
                zIndex: 1000,
                overflow: 'hidden'
              }}>
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ fontWeight: '600', color: '#1a1a1a' }}>{userData.firstName} {userData.lastName}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{userData.email}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{userData.organisation?.name}</div>
                </div>
                <div style={{ padding: '0.5rem' }}>
                  <Link 
                    to="/settings" 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      padding: '0.625rem 0.75rem', 
                      color: '#374151',
                      textDecoration: 'none',
                      borderRadius: '6px'
                    }}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <i className="fas fa-cog"></i>
                    Settings
                  </Link>
                  <button 
                    onClick={handleLogout}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      padding: '0.625rem 0.75rem', 
                      color: '#dc2626',
                      background: 'none',
                      border: 'none',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Sidebar */}
      <aside className="modern-sidebar">
        {/* Brand Logo */}
        <Link to="/dashboard" className="modern-brand-link">
          <div className="brand-icon">
            <i className="fas fa-shield-alt"></i>
          </div>
          <span className="brand-text">SafeWork360</span>
        </Link>

        {/* Sidebar Menu - closing on any nav click (via bubbling) means a
            mobile user doesn't have to separately tap the overlay again
            after picking a destination. */}
        <nav className="modern-sidebar-nav" onClick={closeSidebar}>
          <ul className="modern-nav-list">
            {/* Dashboard */}
            <li className="modern-nav-item">
              <Link to="/dashboard" className={`modern-nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </Link>
            </li>

            {/* Projects */}
            <li className="modern-nav-item">
              <Link to="/projects" className={`modern-nav-link ${location.pathname === '/projects' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-project-diagram"></i>
                <span>Projects</span>
              </Link>
            </li>

            {/* Inspections */}
            <li className="modern-nav-item">
              <Link to="/inspections" className={`modern-nav-link ${location.pathname === '/inspections' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-clipboard-check"></i>
                <span>Inspections</span>
              </Link>
            </li>

            {/* My Tasks - Only for field officers/inspectors */}
            {canViewMyTasks.includes(userData.role) && (
              <li className="modern-nav-item">
                <Link to="/my-tasks" className={`modern-nav-link ${location.pathname === '/my-tasks' ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-tasks"></i>
                  <span>My Tasks</span>
                  <span className="task-badge">5</span>
                </Link>
              </li>
            )}

            {/* Incidents */}
            <li className="modern-nav-item">
              <Link to="/incidents" className={`modern-nav-link ${location.pathname === '/incidents' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-exclamation-triangle"></i>
                <span>Incidents</span>
              </Link>
            </li>

            {/* Monitoring */}
            <li className="modern-nav-item">
              <Link to="/monitoring/tasks" className={`modern-nav-link ${location.pathname.startsWith('/monitoring') ? 'active' : ''}`}>
                <i className="nav-icon fas fa-chart-line"></i>
                <span>Monitoring</span>
              </Link>
            </li>

            {/* EIA Certificates & Conditions - hidden once the org has said
                it doesn't have a formal EIA certificate to track. */}
            {organisation?.usesEiaCertificates !== false && (
              <li className="modern-nav-item">
                <Link to="/eia/certificates" className={`modern-nav-link ${location.pathname.startsWith('/eia') ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-file-contract"></i>
                  <span>EIA Certificates</span>
                </Link>
              </li>
            )}

            {/* Supporting Documents - hidden for now (feature not built yet).
                Re-enable this <li> once the page exists. */}

            {/* Forms - shareable public intake links (anonymous GBV reports,
                stakeholder consultation, road safety tips, etc.); matches
                the backend's requireAccessLevel('manager') create/edit gate. */}
            {(userData.accessLevel === 'admin' || userData.accessLevel === 'manager') && (
              <li className="modern-nav-item">
                <Link to="/forms" className={`modern-nav-link ${location.pathname.startsWith('/forms') ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-file-signature"></i>
                  <span>Forms</span>
                </Link>
              </li>
            )}

            {/* Reports */}
            <li className="modern-nav-item">
              <Link to="/reports" className={`modern-nav-link ${location.pathname === '/reports' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-chart-bar"></i>
                <span>Reports</span>
              </Link>
            </li>

            {/* Team - admin only (backend restricts /auth/users to
                requireAccessLevel('admin')); hidden for everyone else. */}
            {userData.accessLevel === 'admin' && (
              <li className="modern-nav-item">
                <Link to="/team" className={`modern-nav-link ${location.pathname === '/team' ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-users"></i>
                  <span>Team</span>
                </Link>
              </li>
            )}

            {/* Help */}
            <li className="modern-nav-item">
              <Link to="/help" className={`modern-nav-link ${location.pathname === '/help' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-question-circle"></i>
                <span>Help</span>
              </Link>
            </li>

            {/* Settings */}
            <li className="modern-nav-item">
              <Link to="/settings" className={`modern-nav-link ${location.pathname === '/settings' ? 'active' : ''}`}>
                <i className="nav-icon fas fa-cog"></i>
                <span>Settings</span>
              </Link>
            </li>

            {/* Organisations - platform-wide view, super_admin only */}
            {userData.role === 'super_admin' && (
              <li className="modern-nav-item">
                <Link to="/admin/organisations" className={`modern-nav-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}>
                  <i className="nav-icon fas fa-globe"></i>
                  <span>Organisations</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* User Panel at Bottom */}
        <div className="modern-user-panel">
          <div className="user-avatar">
            <i className="fas fa-user-circle"></i>
          </div>
          <div className="user-info">
            <div className="user-name">{userData.firstName} {userData.lastName}</div>
            <div className="user-org">{userData.organisation?.name}</div>
          </div>
        </div>
      </aside>

      {/* Content Wrapper */}
      <main className="app-main">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <strong>Copyright &copy; 2026 SafeWork360.</strong> All rights reserved.
      </footer>

      {needsOnboarding && (
        <OrgOnboardingModal
          organisation={organisation}
          onComplete={(updated) => setOrganisation(updated)}
        />
      )}
    </div>
  );
};
